import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AgeGroup } from '../types/child';

export type ParentCurriculumMode = 'guided' | 'balanced' | 'autopilot' | 'locked' | 'sequenced';
export type ParentCurriculumItemType = 'course-lesson' | 'activity' | 'game' | 'break';
export type ParentCurriculumSource = 'parent' | 'template' | 'suggestion';

export interface ParentCurriculumQueueItem {
  id: string;
  childId: string;
  itemType: ParentCurriculumItemType;
  itemId: string;
  courseId: string;
  lessonId: string;
  ageGroup: AgeGroup;
  title: string;
  courseTitle: string;
  courseEmoji: string;
  courseColor: string;
  category: string;
  subject: string;
  futureSkills: string[];
  durationMinutes: number;
  route: string;
  routeParams?: Record<string, string>;
  required: boolean;
  source: ParentCurriculumSource;
  scheduledDate: string | null;
  pinnedDate: string | null;
  status: 'queued' | 'completed' | 'dismissed';
  createdAt: string;
  updatedAt: string;
}

export interface ParentCurriculumSettings {
  mode: ParentCurriculumMode;
  dailyLessonLimit: number;
  dailyTimeLimitMinutes: number;
  allowRecommendations: boolean;
  childCanSkipQueuedLessons: boolean;
  allowAgeStretch: boolean;
  requireQueueBeforeExtras: boolean;
  lockNavigationToQueue: boolean;
  hiddenCourseCategories: string[];
  learningGoals: string[];
}

export interface AddParentCurriculumLessonInput {
  childId: string;
  itemType?: ParentCurriculumItemType;
  itemId?: string;
  courseId: string;
  lessonId: string;
  ageGroup: AgeGroup;
  title: string;
  courseTitle: string;
  courseEmoji: string;
  courseColor: string;
  category: string;
  subject: string;
  futureSkills: string[];
  durationMinutes?: number;
  route?: string;
  routeParams?: Record<string, string>;
  required?: boolean;
  source?: ParentCurriculumSource;
  scheduledDate?: string | null;
  pinnedDate?: string | null;
}

interface ParentCurriculumState {
  queue: ParentCurriculumQueueItem[];
  settingsByChild: Record<string, Partial<ParentCurriculumSettings>>;
  addLessonToQueue: (input: AddParentCurriculumLessonInput) => string;
  addLessonsToQueue: (inputs: AddParentCurriculumLessonInput[]) => string[];
  removeQueueItem: (childId: string, itemId: string) => void;
  dismissQueueItem: (childId: string, itemId: string) => void;
  markQueueItemCompleted: (childId: string, itemId: string) => void;
  markLessonCompleted: (childId: string, courseId: string, lessonId: string, ageGroup?: AgeGroup) => void;
  moveQueueItem: (childId: string, itemId: string, direction: 'up' | 'down') => void;
  updateQueueItem: (childId: string, itemId: string, patch: Partial<Pick<ParentCurriculumQueueItem, 'required' | 'scheduledDate' | 'pinnedDate' | 'status'>>) => void;
  clearCompleted: (childId: string) => void;
  updateSettings: (childId: string, patch: Partial<ParentCurriculumSettings>) => void;
  getQueueForChild: (childId: string) => ParentCurriculumQueueItem[];
  getActiveQueueForChild: (childId: string) => ParentCurriculumQueueItem[];
  getItemsForToday: (childId: string, date: string) => ParentCurriculumQueueItem[];
  getCurrentSequenceItem: (childId: string, date: string) => ParentCurriculumQueueItem | null;
}

export const DEFAULT_PARENT_CURRICULUM_SETTINGS: ParentCurriculumSettings = {
  mode: 'balanced',
  dailyLessonLimit: 3,
  dailyTimeLimitMinutes: 45,
  allowRecommendations: true,
  childCanSkipQueuedLessons: true,
  allowAgeStretch: true,
  requireQueueBeforeExtras: false,
  lockNavigationToQueue: false,
  hiddenCourseCategories: [],
  learningGoals: [],
};

export function getParentCurriculumSettings(
  state: Pick<ParentCurriculumState, 'settingsByChild'>,
  childId: string,
): ParentCurriculumSettings {
  return {
    ...DEFAULT_PARENT_CURRICULUM_SETTINGS,
    ...(state.settingsByChild[childId] ?? {}),
  };
}

function newQueueId(input: Pick<AddParentCurriculumLessonInput, 'childId' | 'courseId' | 'lessonId' | 'ageGroup'>): string {
  return [
    'parent-lesson',
    input.childId,
    input.courseId,
    input.lessonId,
    input.ageGroup,
    Date.now().toString(36),
    Math.random().toString(36).slice(2, 8),
  ].join('-');
}

function isSameLesson(
  item: ParentCurriculumQueueItem,
  input: Pick<AddParentCurriculumLessonInput, 'childId' | 'itemType' | 'courseId' | 'lessonId' | 'ageGroup'>,
): boolean {
  return item.childId === input.childId
    && item.itemType === (input.itemType ?? 'course-lesson')
    && item.courseId === input.courseId
    && item.lessonId === input.lessonId
    && item.ageGroup === input.ageGroup;
}

function sortForToday(
  items: ParentCurriculumQueueItem[],
  date: string,
): ParentCurriculumQueueItem[] {
  return [...items].sort((a, b) => {
    const aPinned = a.pinnedDate === date ? 0 : 1;
    const bPinned = b.pinnedDate === date ? 0 : 1;
    if (aPinned !== bPinned) return aPinned - bPinned;

    const aScheduled = a.scheduledDate === date ? 0 : 1;
    const bScheduled = b.scheduledDate === date ? 0 : 1;
    if (aScheduled !== bScheduled) return aScheduled - bScheduled;

    return a.createdAt.localeCompare(b.createdAt);
  });
}

export const useParentCurriculumStore = create<ParentCurriculumState>()(
  persist(
    (set, get) => ({
      queue: [],
      settingsByChild: {},

      addLessonToQueue: (input) => {
        const now = new Date().toISOString();
        const existing = get().queue.find((item) => isSameLesson(item, input) && item.status === 'queued');
        if (existing) {
          set((state) => ({
            queue: state.queue.map((item) =>
              item.id === existing.id
                ? {
                    ...item,
                    required: input.required ?? item.required,
                    scheduledDate: input.scheduledDate ?? item.scheduledDate,
                    pinnedDate: input.pinnedDate ?? item.pinnedDate,
                    updatedAt: now,
                  }
                : item,
            ),
          }));
          return existing.id;
        }

        const id = newQueueId(input);
        const item: ParentCurriculumQueueItem = {
          id,
          childId: input.childId,
          itemType: input.itemType ?? 'course-lesson',
          itemId: input.itemId ?? `${input.courseId}:${input.lessonId}`,
          courseId: input.courseId,
          lessonId: input.lessonId,
          ageGroup: input.ageGroup,
          title: input.title,
          courseTitle: input.courseTitle,
          courseEmoji: input.courseEmoji,
          courseColor: input.courseColor,
          category: input.category,
          subject: input.subject,
          futureSkills: input.futureSkills,
          durationMinutes: input.durationMinutes ?? 15,
          route: input.route ?? '/courses',
          routeParams: input.routeParams ?? { courseId: input.courseId, lessonId: input.lessonId },
          required: input.required ?? true,
          source: input.source ?? 'parent',
          scheduledDate: input.scheduledDate ?? null,
          pinnedDate: input.pinnedDate ?? null,
          status: 'queued',
          createdAt: now,
          updatedAt: now,
        };
        set((state) => ({ queue: [...state.queue, item] }));
        return id;
      },

      addLessonsToQueue: (inputs) => inputs.map((input) => get().addLessonToQueue(input)),

      removeQueueItem: (childId, itemId) => {
        set((state) => ({
          queue: state.queue.filter((item) => !(item.childId === childId && item.id === itemId)),
        }));
      },

      dismissQueueItem: (childId, itemId) => {
        get().updateQueueItem(childId, itemId, { status: 'dismissed' });
      },

      markQueueItemCompleted: (childId, itemId) => {
        get().updateQueueItem(childId, itemId, { status: 'completed' });
      },

      markLessonCompleted: (childId, courseId, lessonId, ageGroup) => {
        set((state) => ({
          queue: state.queue.map((item) => {
            const matches = item.childId === childId
              && item.courseId === courseId
              && item.lessonId === lessonId
              && (!ageGroup || item.ageGroup === ageGroup)
              && item.status === 'queued';
            return matches
              ? { ...item, status: 'completed', updatedAt: new Date().toISOString() }
              : item;
          }),
        }));
      },

      moveQueueItem: (childId, itemId, direction) => {
        set((state) => {
          const childItems = state.queue.filter((item) => item.childId === childId && item.status === 'queued');
          const idx = childItems.findIndex((item) => item.id === itemId);
          const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
          if (idx < 0 || swapIdx < 0 || swapIdx >= childItems.length) return state;

          const reordered = [...childItems];
          [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
          const reorderedIds = reordered.map((item) => item.id);
          let childCursor = 0;

          return {
            queue: state.queue.map((item) => {
              if (item.childId !== childId || item.status !== 'queued') return item;
              const replacement = state.queue.find((q) => q.id === reorderedIds[childCursor++]);
              return replacement ?? item;
            }),
          };
        });
      },

      updateQueueItem: (childId, itemId, patch) => {
        set((state) => ({
          queue: state.queue.map((item) =>
            item.childId === childId && item.id === itemId
              ? { ...item, ...patch, updatedAt: new Date().toISOString() }
              : item,
          ),
        }));
      },

      clearCompleted: (childId) => {
        set((state) => ({
          queue: state.queue.filter((item) => item.childId !== childId || item.status !== 'completed'),
        }));
      },

      updateSettings: (childId, patch) => {
        set((state) => ({
          settingsByChild: {
            ...state.settingsByChild,
            [childId]: {
              ...(state.settingsByChild[childId] ?? {}),
              ...patch,
            },
          },
        }));
      },

      getQueueForChild: (childId) => get().queue.filter((item) => item.childId === childId),

      getActiveQueueForChild: (childId) =>
        get().queue.filter((item) => item.childId === childId && item.status === 'queued'),

      getItemsForToday: (childId, date) => {
        const settings = getParentCurriculumSettings(get(), childId);
        const active = get().queue.filter((item) => {
          if (item.childId !== childId || item.status !== 'queued') return false;
          if (settings.hiddenCourseCategories.includes(item.category)) return false;
          if (item.pinnedDate === date || item.scheduledDate === date) return true;
          if (settings.mode === 'autopilot') return false;
          return item.scheduledDate === null;
        });

        return sortForToday(active, date).slice(0, settings.dailyLessonLimit);
      },

      getCurrentSequenceItem: (childId, date) => get().getItemsForToday(childId, date)[0] ?? null,
    }),
    {
      name: 'homeschool-parent-curriculum',
      version: 2,
      migrate: (state) => {
        const migrated = state as Partial<ParentCurriculumState> | undefined;
        return {
          ...migrated,
          queue: (migrated?.queue ?? []).map((item) => ({
            ...item,
            itemType: item.itemType ?? 'course-lesson',
            itemId: item.itemId ?? `${item.courseId}:${item.lessonId}`,
            route: item.route ?? '/courses',
            routeParams: item.routeParams ?? { courseId: item.courseId, lessonId: item.lessonId },
          })),
          settingsByChild: migrated?.settingsByChild ?? {},
        } as ParentCurriculumState;
      },
    },
  ),
);
